import { supabase } from './supabase';
import { anonymousSupabase, validateAnonymousMode, getSessionDiagnostics } from './anonymous-client';
import { SupabaseClient } from '@supabase/supabase-js';

/**
 * Storage configuration and validation utilities for Supabase Storage
 *
 * CRITICAL: This module uses the anonymous Supabase client for audio uploads
 * to ensure feedback submissions work even when users are logged into the main app.
 */

export const STORAGE_CONFIG = {
  BUCKET_NAME: 'feedback-audio',
  MAX_FILE_SIZE: 52428800, // 50MB in bytes
  ALLOWED_MIME_TYPES: [
    'audio/mpeg',
    'audio/wav',
    'audio/mp4',
    'audio/ogg',
    'audio/webm',
    'audio/x-m4a',
  ],
  MAX_DURATION_SECONDS: 1800, // 30 minutes
} as const;

export interface StorageValidationResult {
  isValid: boolean;
  error?: string;
  details?: string;
}

/**
 * Validates that the feedback-audio bucket exists and is accessible
 */
export async function validateStorageBucket(): Promise<StorageValidationResult> {
  try {
    console.log('[Storage] Validating feedback-audio bucket...');

    const { data, error } = await supabase.storage.getBucket(STORAGE_CONFIG.BUCKET_NAME);

    if (error) {
      console.error('[Storage] Bucket validation error:', error);

      if (error.message?.includes('not found')) {
        return {
          isValid: false,
          error: 'Storage bucket not found',
          details: `The '${STORAGE_CONFIG.BUCKET_NAME}' bucket does not exist. Please run database migrations to create it.`,
        };
      }

      return {
        isValid: false,
        error: 'Storage bucket validation failed',
        details: error.message,
      };
    }

    if (!data) {
      return {
        isValid: false,
        error: 'Storage bucket validation failed',
        details: 'Bucket exists but returned no data',
      };
    }

    console.log('[Storage] Bucket validation successful:', {
      id: data.id,
      name: data.name,
      public: data.public,
    });

    return { isValid: true };
  } catch (err: any) {
    console.error('[Storage] Unexpected error during bucket validation:', err);
    return {
      isValid: false,
      error: 'Storage validation failed',
      details: err.message || 'Unknown error occurred',
    };
  }
}

/**
 * Validates an audio file before upload
 */
export function validateAudioFile(blob: Blob): StorageValidationResult {
  if (!blob) {
    return {
      isValid: false,
      error: 'No audio file provided',
      details: 'The audio blob is null or undefined',
    };
  }

  if (blob.size === 0) {
    return {
      isValid: false,
      error: 'Audio file is empty',
      details: 'The audio blob has 0 bytes',
    };
  }

  if (blob.size > STORAGE_CONFIG.MAX_FILE_SIZE) {
    const sizeMB = (blob.size / 1024 / 1024).toFixed(2);
    const maxSizeMB = (STORAGE_CONFIG.MAX_FILE_SIZE / 1024 / 1024).toFixed(0);
    return {
      isValid: false,
      error: 'Audio file is too large',
      details: `File size is ${sizeMB}MB, maximum allowed is ${maxSizeMB}MB`,
    };
  }

  const isValidType = STORAGE_CONFIG.ALLOWED_MIME_TYPES.includes(blob.type);
  if (!isValidType && blob.type) {
    return {
      isValid: false,
      error: 'Invalid audio format',
      details: `File type '${blob.type}' is not allowed. Allowed types: ${STORAGE_CONFIG.ALLOWED_MIME_TYPES.join(', ')}`,
    };
  }

  return { isValid: true };
}

/**
 * Uploads an audio file to Supabase Storage with validation and error handling
 *
 * CRITICAL FIX: Uses anonymous client to prevent auth session interference
 * This ensures uploads work in both incognito and normal browser sessions
 */
export async function uploadAudioFile(
  blob: Blob,
  requestId: string,
  options: { onProgress?: (progress: number) => void } = {}
): Promise<{ url: string | null; error?: string }> {
  const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substring(7)}`;

  console.log(`[${uploadId}] ═══ AUDIO UPLOAD START ═══`);
  console.log(`[${uploadId}] Request ID: ${requestId}`);
  console.log(`[${uploadId}] Blob size: ${blob.size} bytes (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
  console.log(`[${uploadId}] Blob type: ${blob.type}`);
  console.log(`[${uploadId}] Target bucket: ${STORAGE_CONFIG.BUCKET_NAME}`);

  // Session diagnostics - helps identify auth contamination issues
  const sessionDiag = await getSessionDiagnostics();
  console.log(`[${uploadId}] Session diagnostics:`, sessionDiag);

  // Validate anonymous mode
  const anonValidation = await validateAnonymousMode(anonymousSupabase);
  console.log(`[${uploadId}] Anonymous mode validation:`, anonValidation);

  if (!anonValidation.isAnonymous) {
    console.warn(`[${uploadId}] ⚠️ Client is not in anonymous mode, but will proceed with anonymous client`);
  }

  // Validate file
  const validation = validateAudioFile(blob);
  if (!validation.isValid) {
    console.error(`[${uploadId}] ❌ File validation failed:`, validation);
    return { url: null, error: validation.error };
  }

  console.log(`[${uploadId}] ✅ File validation passed`);

  const fileName = `${requestId}-${Date.now()}.webm`;
  console.log(`[${uploadId}] Generated filename: ${fileName}`);

  // First attempt: Use anonymous client (this should always work)
  try {
    console.log(`[${uploadId}] [ATTEMPT 1] Using anonymous client for upload`);
    const result = await attemptUpload(anonymousSupabase, fileName, blob, uploadId);
    if (result.success) {
      return { url: result.url! };
    }

    console.warn(`[${uploadId}] [ATTEMPT 1] Failed with anonymous client:`, result.error);

    // Second attempt: Fallback to default client (in case of network issues)
    console.log(`[${uploadId}] [ATTEMPT 2] Falling back to default client`);
    const fallbackResult = await attemptUpload(supabase, fileName, blob, uploadId);
    if (fallbackResult.success) {
      console.log(`[${uploadId}] ⚠️ Upload succeeded with default client (not anonymous)`);
      return { url: fallbackResult.url! };
    }

    // Both attempts failed
    console.error(`[${uploadId}] ❌ Both upload attempts failed`);
    return {
      url: null,
      error: result.error || 'Upload failed after multiple attempts'
    };
  } catch (err: any) {
    console.error(`[${uploadId}] ❌ Unexpected upload error:`, err);
    console.error(`[${uploadId}] Error stack:`, err.stack);
    return {
      url: null,
      error: `Upload failed: ${err.message || 'Unknown error'}`,
    };
  }
}

/**
 * Attempts to upload a file using the specified Supabase client
 */
async function attemptUpload(
  client: SupabaseClient,
  fileName: string,
  blob: Blob,
  uploadId: string
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    console.log(`[${uploadId}] Starting upload to bucket: ${STORAGE_CONFIG.BUCKET_NAME}`);
    const { data: uploadData, error: uploadError } = await client.storage
      .from(STORAGE_CONFIG.BUCKET_NAME)
      .upload(fileName, blob, {
        contentType: blob.type || 'audio/webm',
        upsert: false,
      });

    if (uploadError) {
      console.error(`[${uploadId}] ❌ Upload failed:`, {
        message: uploadError.message,
        name: uploadError.name,
        cause: uploadError.cause,
      });

      // Provide specific error messages
      let errorMessage = 'Failed to upload audio';
      let detailedError = uploadError.message;

      if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('bucket')) {
        errorMessage = 'Storage bucket not found. Please contact support.';
      } else if (uploadError.message?.includes('exceeded') || uploadError.message?.includes('too large')) {
        errorMessage = 'Audio file is too large. Please record a shorter message.';
      } else if (uploadError.message?.includes('JWT') || uploadError.message?.includes('auth') || uploadError.message?.includes('Unauthorized')) {
        errorMessage = 'Authentication error. Please refresh the page and try again.';
      } else if (uploadError.message?.includes('network') || uploadError.message?.includes('fetch') || uploadError.message?.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (uploadError.message?.includes('timeout')) {
        errorMessage = 'Upload timed out. Please try again.';
      } else if (uploadError.message?.includes('row-level security') || uploadError.message?.includes('policy')) {
        errorMessage = 'Permission denied. Please refresh the page and try again.';
      } else {
        errorMessage = `Upload failed: ${detailedError}`;
      }

      console.error(`[${uploadId}] Error category: ${errorMessage}`);
      return { success: false, error: errorMessage };
    }

    if (!uploadData?.path) {
      console.error(`[${uploadId}] ❌ Upload succeeded but no path returned`);
      return { success: false, error: 'Upload completed but file path is missing' };
    }

    console.log(`[${uploadId}] ✅ Upload successful`);
    console.log(`[${uploadId}] Uploaded path: ${uploadData.path}`);

    // Generate public URL using the same client that uploaded the file
    const { data: { publicUrl } } = client.storage
      .from(STORAGE_CONFIG.BUCKET_NAME)
      .getPublicUrl(uploadData.path);

    console.log(`[${uploadId}] ✅ Public URL generated: ${publicUrl}`);
    console.log(`[${uploadId}] ═══ AUDIO UPLOAD COMPLETE ═══`);

    return { success: true, url: publicUrl };
  } catch (err: any) {
    console.error(`[${uploadId}] ❌ Unexpected upload error:`, err);
    console.error(`[${uploadId}] Error stack:`, err.stack);
    return {
      success: false,
      error: `Upload failed: ${err.message || 'Unknown error'}`,
    };
  }
}

/**
 * Gets the public URL for an audio file
 */
export function getAudioUrl(filePath: string): string {
  const { data } = supabase.storage
    .from(STORAGE_CONFIG.BUCKET_NAME)
    .getPublicUrl(filePath);

  return data.publicUrl;
}

/**
 * Deletes an audio file from storage
 */
export async function deleteAudioFile(filePath: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.storage
      .from(STORAGE_CONFIG.BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error('[Storage] Delete failed:', error);
      return { success: false, error: error.message };
    }

    console.log('[Storage] File deleted:', filePath);
    return { success: true };
  } catch (err: any) {
    console.error('[Storage] Unexpected delete error:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}
